/**
 * 工程文件的 schemaVersion 与迁移框架（P0-1c）
 *
 * 为什么现在就要：画布文档与导演台工程会被用户长期保存（本地 + 服务器），
 * 而字段一定会演进。没有版本与迁移，后果就是"升级后老工程打不开"或更糟——
 * 静默按新结构读老数据，把用户作品读坏。
 *
 * 三条设计原则：
 *  1. **只向前迁移**：遇到高于当前代码支持的版本，明确拒绝，绝不"尽力解析"。
 *     用户用新版保存过工程后，老版本程序必须拒绝打开，而不是猜。
 *  2. **迁移函数为纯函数**：不改动入参，失败即整体放弃，不留半成品。
 *  3. **版本链必须连续**：注册时校验 from→to 逐级衔接，缺一级就报错，
 *     避免"跳过 v3 直接到 v4"导致中间字段补全被漏掉。
 */

export type DocKind = 'canvas' | 'directorProject';

export interface Versioned {
  schemaVersion: number;
}

export type MigrationFn = (doc: Record<string, unknown>) => Record<string, unknown>;

export interface MigrationStep {
  from: number;
  to: number;
  run: MigrationFn;
}

export type MigrateResult<T> =
  | { ok: true; value: T; from: number; to: number; migrated: boolean }
  | {
      ok: false;
      reason: 'FUTURE_VERSION' | 'MISSING_VERSION' | 'BROKEN_CHAIN' | 'NO_PATH';
      message: string;
      version: number;
    };

/** 各文档类型当前代码支持的版本。新增迁移时同步 +1。 */
export const CURRENT_VERSION: Record<DocKind, number> = {
  canvas: 1,
  directorProject: 1,
};

const REGISTRY: Record<DocKind, MigrationStep[]> = {
  canvas: [],
  directorProject: [],
};

export function registerMigrations(kind: DocKind, steps: readonly MigrationStep[]): void {
  const sorted = [...steps].sort((a, b) => a.from - b.from);

  // 链必须逐级 +1 且首尾相接，终点等于当前版本；
  // 否则会出现“跳过某一级、字段补全被漏掉”的静默数据损坏。
  let expect: number | null = null;
  for (const s of sorted) {
    if (s.to !== s.from + 1) {
      throw new Error(`${kind} 迁移步必须逐级 +1：发现 ${s.from}->${s.to}`);
    }
    if (expect !== null && s.from !== expect) {
      throw new Error(`${kind} 迁移链断裂：期望 from=${expect}，实际 ${s.from}`);
    }
    expect = s.to;
  }
  if (sorted.length > 0 && sorted[sorted.length - 1]?.to !== CURRENT_VERSION[kind]) {
    throw new Error(
      `${kind} 迁移链终点 ${sorted[sorted.length - 1]?.to} 与 CURRENT_VERSION ${CURRENT_VERSION[kind]} 不一致`,
    );
  }
  REGISTRY[kind] = sorted;
}

/**
 * 读版本。返回 null 表示字段缺失或非法。
 *
 * 为何不“缺字段就当 v0”：本系统从第一个版本起就会写 schemaVersion，
 * 因此缺这个字段的文档不是“我们的老工程”，而是外来或已损数据。
 * 把它猜成 v0 会静默跑一套不适用的迁移，比直接拒绝危险得多。
 */
export function readVersion(doc: unknown): number | null {
  if (typeof doc !== 'object' || doc === null || Array.isArray(doc)) {
    return null;
  }
  const v = (doc as Record<string, unknown>).schemaVersion;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 0) {
    return null;
  }
  return v;
}

export function migrate<T extends Versioned>(kind: DocKind, doc: unknown): MigrateResult<T> {
  if (typeof doc !== 'object' || doc === null || Array.isArray(doc)) {
    return { ok: false, reason: 'NO_PATH', version: 0, message: `${kind} 文档不是合法对象，无法读取` };
  }

  const version = readVersion(doc);
  if (version === null) {
    return {
      ok: false,
      reason: 'MISSING_VERSION',
      version: 0,
      message: `${kind} 文档缺少合法的 schemaVersion 字段，无法判定结构版本，已拒绝打开以免误改数据。`,
    };
  }

  const target = CURRENT_VERSION[kind];
  if (version > target) {
    return {
      ok: false,
      reason: 'FUTURE_VERSION',
      version,
      message: `该工程由更新版本创建（schemaVersion=${version}，当前仅支持到 ${target}），请用新版程序打开。`,
    };
  }

  const steps = REGISTRY[kind];
  let cursor = version;
  let current: Record<string, unknown> = { ...(doc as Record<string, unknown>) };

  while (cursor < target) {
    const step = steps.find((s) => s.from === cursor);
    if (step === undefined) {
      return {
        ok: false,
        reason: 'BROKEN_CHAIN',
        version,
        message: `${kind} 缺少 v${cursor} -> v${cursor + 1} 的迁移步骤，已拒绝打开以免损坏工程。`,
      };
    }
    current = step.run(current);
    cursor = step.to;
  }

  current.schemaVersion = target;
  return { ok: true, value: current as unknown as T, from: version, to: target, migrated: version !== target };
}

/**
 * 便捷入口：迁移失败时抛错。
 * UI 层应优先用 migrate() 拿到可读 message 去弹提示；只有工具脚本/测试才用它。
 */
export function migrateOrThrow<T extends Versioned>(kind: DocKind, doc: unknown): T {
  const r = migrate<T>(kind, doc);
  if (r.ok) {
    return r.value;
  }
  throw new Error(r.message);
}
